import {
  SecondaryButton,
  FormError,
  Heading_2 as Body2,
} from '@huggg/react-components';
import classnames from 'classnames';
import { InfoDialog } from 'components/ui';
import dictionary from 'config/dictionary';
import { Formik, Form, Field } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { e164 } from 'utilities/telephoneFormatter';
import * as Yup from 'yup';
import styles from './form.module.scss';

const E164_LENGTH_UK = 13;

Yup.addMethod(Yup.string, 'validNumber', function (message) {
  return this.test('valid-number', message, function (value) {
    const { path, createError } = this;
    const formatted = e164(value);

    return (
      (formatted && formatted.length === E164_LENGTH_UK) ||
      createError({ path, message })
    );
  });
});

const baseValidationSchema = Yup.object().shape({
  number: Yup.string()
    .required(dictionary.forms.phoneNumber.fields.number.error.required)
    .min(11)
    .max(20)
    .validNumber(),
});

const UIFormPhoneNumber = (props) => {
  const {
    additionalValidator,
    button: ButtonComponent,
    onSubmit,
    ...rest
  } = props;

  const infoDialogRef = useRef();
  const openButtonRef = useRef();
  const inputRef = useRef();
  const isFirstMount = useRef(true);

  const validationSchema = !additionalValidator
    ? baseValidationSchema
    : Yup.object().shape({
        number: Yup.reach(baseValidationSchema, 'number').concat(
          Yup.string().test(
            'additionalValidator',
            'Number does not meet restrictions',
            additionalValidator,
          ),
        ),
      });

  const initialValues = {
    number:
      (process.env.NODE_ENV === 'development' &&
        process.env.REACT_APP_PHONE_NUMBER) ||
      '',
  };

  const onNumberChange = (event, { validateForm, setFieldValue }) => {
    const value = event.target.value;
    const formatted = value
      .replace(/(?!^[+\d])[^\d\s]/g, '')
      .replace(/^\s+/g, '');
    setFieldValue('number', formatted);
    validateForm();
  };

  const onFormSubmit = (values, formikBag) => {
    const formattedValues = {
      ...values,
      number: e164(values.number),
    };

    onSubmit(formattedValues, formikBag);
  };

  useEffect(() => {
    isFirstMount.current = false;
  }, []);

  const renderErrorMessage = (className, message) => (
    <div className={styles.errorContainer}>
      <FormError className={className}>
        {/required|try again/.test(message) ? (
          message
        ) : (
          <span>
            {dictionary.forms.phoneNumber.fields.number.error.invalid.text}{' '}
            <button
              type="button"
              onClick={infoDialogRef.current.open}
              className={styles.errorMessageButton}
              ref={openButtonRef}
            >
              {dictionary.forms.phoneNumber.fields.number.error.invalid.button}
            </button>
          </span>
        )}
      </FormError>
    </div>
  );

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={onFormSubmit}
        validationSchema={validationSchema}
      >
        {(formikProps) => {
          const { errors, setFieldValue, touched, validateForm } = formikProps;

          /**
           * Hack to get the form to validate on the initial mount
           */
          if (isFirstMount.current) {
            setTimeout(validateForm, 10);
          }

          const hasError = touched.number && errors.number;

          return (
            <Form {...rest}>
              <label htmlFor="email" className={styles.label}>
                {dictionary.forms.phoneNumber.fields.number.label}
              </label>
              <Field
                type="tel"
                id="number"
                name="number"
                maxLength="20"
                data-test-id="stash-submit-input"
                innerRef={inputRef}
                className={classnames(styles.input, styles.inputTextCenter, {
                  [styles.inputError]: errors.number && touched.number,
                })}
                onChange={(event) =>
                  onNumberChange(event, {
                    setFieldValue,
                    validateForm,
                  })
                }
              />

              {hasError &&
                renderErrorMessage(styles.errorMessage, errors.number)}

              {React.cloneElement(ButtonComponent, {
                type: 'submit',
                disabled:
                  Object.keys(errors).length > 0 || formikProps.isSubmitting,
              })}
            </Form>
          );
        }}
      </Formik>

      <InfoDialog
        ref={infoDialogRef}
        openButtonRef={openButtonRef}
        body={
          <Body2>
            <div className={styles.validationRulesMessage}>
              {dictionary.forms.phoneNumber.dialogs.number.title}
            </div>

            <ul className={styles.validationRules}>
              {dictionary.forms.phoneNumber.dialogs.number.body.map(
                (point, index) => (
                  <li className={styles.validationRule} key={index}>
                    {point}
                  </li>
                ),
              )}
            </ul>
          </Body2>
        }
      />
    </>
  );
};

UIFormPhoneNumber.defaultProps = {
  button: <SecondaryButton />,
};

UIFormPhoneNumber.propTypes = {
  additionalValidator: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  button: PropTypes.element,
};

export default UIFormPhoneNumber;
